import { Theme, AssetType, TextStyleProperties, Asset, TextAssetProperties, NewsAssetProperties, DateTimeAssetProperties, ClockAssetProperties, StocksAssetProperties, PIAssetProperties, FloorAssetProperties } from 'shared/models';
import { values, mapValues } from "lodash";

export const upgradeThemeCoordinateSystem = (theme: Theme): Theme => {
    // Bail out if this theme has no containers larger than 10 units of width or height
    // (which we're using as our heuristic to determine if the theme is on the 0-1 scale)
    if (!values(theme.containers).find(({ width, height }) => width > 10 || height > 10)) {
        return theme;
    }

    // Convert all the container coordinates to be on a scale from 0 - 1
    const containers = mapValues(theme.containers, container => {
        const { left, top, width, height } = container;
        return {
            ...container,
            left: left / theme.width,
            top: top / theme.height,
            width: width / theme.width,
            height: height / theme.height,
        };
    });

    // Convert all the assets that have sizes
    const assets = mapValues(theme.assets, asset => {
        const withProperties = <T>(fn: (props: T) => Partial<T>): Asset => {
            return {
                ...asset,
                properties: {
                    ...(asset.properties as any),
                    ...fn(asset.properties as any as T),
                },
            };
        };
        const withScaledTextStyle = (style: TextStyleProperties): TextStyleProperties => style && ({
            ...style,
            size: style.size / theme.height,
        });

        switch (asset.assetType) {
            case AssetType.Text:
                return withProperties<TextAssetProperties>(props => ({
                    size: props.size / theme.height,
                }));

            case AssetType.News:
                return withProperties<NewsAssetProperties>(props => ({
                    headline: withScaledTextStyle(props.headline),
                    content: withScaledTextStyle(props.content),
                }));

            case AssetType.DateTime:
                return withProperties<DateTimeAssetProperties>(props => ({
                    size: props.size / theme.height,
                }));
    
            case AssetType.Clock:
                return withProperties<ClockAssetProperties>(props => ({
                    size: props.size / theme.height,
                }));
                
            case AssetType.Stocks:
                return withProperties<StocksAssetProperties>(props => ({
                    size: props.size / theme.height,
                }));

            case AssetType.MaxPersons:
                return withProperties<PIAssetProperties>(props => ({
                    size: props.size / theme.height,
                }));
    
            case AssetType.MaxWeight:
                return withProperties<PIAssetProperties>(props => ({
                    size: props.size / theme.height,
                }));
    
            case AssetType.ElevatorId:
                return withProperties<PIAssetProperties>(props => ({
                    size: props.size / theme.height,
                }));
    
            case AssetType.Floor:
                return withProperties<FloorAssetProperties>(props => ({
                    size: props.size / theme.height,
                    minTextHeight: (props.minTextHeight || 0) / theme.height,
                }));
    
            case AssetType.Image: // width and height are real values for the image
            case AssetType.Video: // width and height are real values for the video
            case AssetType.Theme: // width and height are the real values for the embedded theme
            case AssetType.Weather: // text size is undefined... calculated automatically by the asset
            case AssetType.Html:
            case AssetType.Direction:
            case AssetType.Logo:
            case AssetType.Traffic:
            default:
                return asset;
        }
    });

    return {
        ...theme,
        containers,
        assets,
    };
};
